import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { STATUS_TYPES } from '../../../common/constants/StatusConstants';
import {
  buildJourneyDesignerFlowViewUrl,
  clearJourneyDesignerReturnContext,
  getJourneyDesignerReturnContext,
  type JourneyDesignerServiceReturnLocationState,
  setServiceUpdatedFlag
} from '../../../common/utilities/JourneyDesignerReturnContext';
import { FORM_SERVICE_TYPE, SERVICE_CATEGORY, SERVICE_MODE, URL_PATH } from '../../constants';
import { IAuthDetailsRequest, IExceptionModel, IExceptionRequest, IServiceRequestModel } from '../../models';
import { AppDispatch, useSelector } from '../../store/AppStore';
import { createServiceRequest } from '../../store/actions/sagaActions/services/CreateServiceSagaActions';
import { clearUpdateServiceData, updateServiceRequest } from '../../store/actions/sagaActions/services/UpdateServiceSagaActions';
import { getServiceNameSuccess } from '../../store/reducer/services/ServiceNameReducer';
import { clearServiceOperationMonitor } from '../../store/reducer/services/ServiceOperationMonitorReducer';
import { getVersionFromUrl, prefixNamespaceMapper, serviceOperationMapper } from '../../utilities/services/ServiceListUtils';

/**
 * @method useSaveServiceAsDraft
 * @description Custom hook to perform update service operation
 * @param dispatchShowToast dispatches the actions
 * @param setViewStatus Callback function to notify UI about update
 * @param setIsEdit Callback function to notify UI update edit
 * @returns isLoading true if loading config else false
 */
export const useSaveServiceAsDraft = (
  success: (title: string, message: string) => void,
  failure: (title: string, message: string) => void,
  warning: (title: string, message: string) => void,
  setViewStatus: (viewStatus: boolean) => void,
  setIsEdit: (isEdit: boolean) => void
) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isUpdateServiceLoading, setIsUpdateServiceLoading] = useState(false);
  const [isUpdateServiceSuccess, setIsUpdateServiceSuccess] = useState(false);
  const [isActiveUpdate, setIsActiveUpdate] = useState(false);
  const { t: translate } = useTranslation();
  const history = useNavigate();
  const location = useLocation();
  const version = getVersionFromUrl();

  const { exceptions } = useSelector((state) => state.exceptionReducer);
  const { success: createServiceSuccess, errorMessage: createServiceErrorMsg } = useSelector((state) => state.serviceReducer);
  const { serviceName, serviceDescription, endPoint, serviceType, authDetails: reducerAuthDetails, soapAttributes: reducerSoapAttributes } = useSelector((state) => state.serviceDetailsReducer);
  const { serviceOperations } = useSelector(
    (state) => state.serviceOperationReducer
  );
  const { errorMessage: updateServiceErrorMsg, success: updateServiceSuccess, serviceData: updateServiceData } = useSelector((state) => state.updateServiceReducer);
  const { ingressEventMappingList } = useSelector(
    (state) => state.ingressEventMappingReducer
  );
  const { egressEventMappingList } = useSelector(
    (state) => state.egressEventMappingReducer
  );
  /**
   * @method updateServiceToDraftState
   * @description Updates the service
   */
  const updateServiceToDraftState = (serviceStatus: string) => {
    setIsUpdateServiceLoading(true);
    const newServiceOperations = [...serviceOperations];
    const requestExceptionList: IExceptionRequest[] = exceptions.map((exception: IExceptionModel) => ({
      ersExceptionName: exception.exceptionId,
      errorCode: `${exception.errorCode}`,
      actions: exception.action
    }));
    const serviceDetails: IServiceRequestModel = {
      serviceName,
      serviceDescription,
      serviceCategory: SERVICE_CATEGORY,
      endPoint,
      serviceType,
      authDetails: reducerAuthDetails?.authType ? {
        authType: reducerAuthDetails?.authType,
        username: reducerAuthDetails?.username,
        password: reducerAuthDetails?.password,
        authMethod: reducerAuthDetails?.authMethod,
        authUrl: reducerAuthDetails?.authUrl,
        headerParam: reducerAuthDetails?.headerParam ? reducerAuthDetails?.headerParam : {},
        requestParam: reducerAuthDetails?.requestParam ? reducerAuthDetails?.requestParam : {},
        queryPathParam: reducerAuthDetails?.queryPathParam ? reducerAuthDetails?.queryPathParam : {},
      } as IAuthDetailsRequest : undefined,
      soapAttributes: serviceType?.toLocaleLowerCase() === FORM_SERVICE_TYPE.SOAPSERVICE ? {
        headersXML: reducerSoapAttributes?.headersXML,
        envelopeNamespaces: reducerSoapAttributes?.envelopeNamespaces ? prefixNamespaceMapper(reducerSoapAttributes?.envelopeNamespaces) : [],
      } : undefined,
      serviceOperations: newServiceOperations
        ? serviceOperationMapper(newServiceOperations, ingressEventMappingList, egressEventMappingList)
        : [],
      serviceExceptions: requestExceptionList
    };
    // if status is active, create new service with draft version, else update the service
    if (serviceStatus?.toLocaleUpperCase() === STATUS_TYPES.ACTIVE) {
      dispatch(createServiceRequest(serviceDetails));
      setIsActiveUpdate(true);
    } else {
      setIsActiveUpdate(false);
      dispatch(updateServiceRequest(serviceDetails, version));
    }
  };

  useEffect(() => {
    if (updateServiceSuccess) {
      setIsUpdateServiceSuccess(true);
      success(
        translate('toast_component.lbl_success'),
        translate('service_wrapper_component.msg_update_service_success_body', {
          replace: { serviceName }
        })
      );
      dispatch(clearServiceOperationMonitor());
      dispatch(getServiceNameSuccess([updateServiceData]));
      setIsEdit(false);
      setViewStatus(true);
      setIsUpdateServiceLoading(false);

      const jdContext = getJourneyDesignerReturnContext();
      if (jdContext) {
        clearJourneyDesignerReturnContext();
        setServiceUpdatedFlag();
        const returnState: JourneyDesignerServiceReturnLocationState = {
          jdServiceReturn: true,
          jdResume: jdContext.serviceReturnResume ?? { kind: 'none' },
          jdResumeSidebar: jdContext.resumeSidebar ?? 'ALL_SERVICES',
        };
        history(buildJourneyDesignerFlowViewUrl(jdContext, 'jdReturn=1'), { state: returnState });
      } else {
        const { state } = location;
        const normalizedViewPath = [
          `${URL_PATH.SERVICE_HOME}${URL_PATH.VIEW_SERVICE}`,
          serviceName,
          updateServiceData?.version || version,
          SERVICE_MODE.VIEW
        ].filter(Boolean).join('/');
        history(
          normalizedViewPath,
          { state: { ...state } }
        );
      }
    } else if (updateServiceErrorMsg) {
      setIsUpdateServiceSuccess(false);
      failure(
        translate('toast_component.lbl_error'),
        translate('service_wrapper_component.msg_update_service_failure_body')
      );
      dispatch(clearServiceOperationMonitor());
      dispatch(clearUpdateServiceData());
      setIsUpdateServiceLoading(false);
    }

    if (createServiceErrorMsg || createServiceSuccess) {
      setIsUpdateServiceLoading(false);
    }
  }, [updateServiceSuccess, updateServiceErrorMsg, updateServiceData, createServiceErrorMsg, createServiceSuccess]);

  return { isUpdateServiceLoading, updateServiceToDraftState, isUpdateServiceSuccess, setIsUpdateServiceSuccess, isActiveUpdate };
};
